import { env } from './env.ts';
import { isApp } from './app-bridge.ts';

/**
 * 안드로이드 앱으로 여는 링크. (2026-10-16)
 *
 * 카카오톡·인스타그램 같은 앱 안에서 링크를 누르면 그 앱의 **인앱 브라우저**로 열린다.
 * 그 안에서는 앱 링크(https)가 우리 앱을 띄우지 않는다. `intent://` 링크는 패키지를
 * 직접 가리키므로 인앱 브라우저에서도 앱이 뜬다.
 *
 *   앱이 있으면   앱이 뜨고 같은 주소(`/event/bumjin1`)로 간다      (MainActivity.deepLinkUrl)
 *   앱이 없으면   플레이스토어 → 설치 → 처음 켤 때 그 주소로 간다   (InstallLink.kt)
 *
 * ★ 스토어 링크의 `referrer` 는 **두 번 인코딩**한다. 플레이스토어가 한 번 풀어서 앱에
 *   `url=%2Fevent%2Fbumjin1` 을 넘기고, 앱이 그것을 다시 풀어 `/event/bumjin1` 을 얻는다.
 */

export const ANDROID_PACKAGE = 'com.urnplay.android';

/** 안드로이드 기기의 브라우저(인앱 브라우저 포함)인가. 우리 앱 안이면 false. */
export function isAndroidBrowser(): boolean {
  return /Android/i.test(navigator.userAgent) && !isApp();
}

/** 앱을 아직 스토어에 올리지 않았으면 배너를 띄우지 않는다 (`VITE_ANDROID_APP=Y` 로 켠다). */
export function isAppReleased(): boolean {
  return env.androidApp;
}

/** 우리 사이트 경로만. `#` 은 intent 문법과 겹치므로 버린다. */
function sitePath(path: string): string {
  const clean = path.split('#')[0] || '/';
  return clean.startsWith('/') && !clean.startsWith('//') ? clean : '/';
}

/**
 * 카카오 공유 카드의 '앱에서 보기' 버튼이 앱에 넘길 값. (2026-10-19)
 *
 * 카카오톡은 이 값을 붙여 `kakao{네이티브 앱 키}://kakaolink?path=%2Fevent%2Fbumjin1` 로
 * 앱을 띄운다(MainActivity.deepLinkUrl). 앱이 없으면 카카오 콘솔에 넣은 마켓 URL 로 간다.
 * 우리 사이트 주소가 아니면 null — 버튼을 달지 않는다.
 */
export function kakaoExecutionParams(url: string): string | null {
  try {
    const parsed = new URL(url, location.origin);
    if (!/^(www\.)?urnplay\.com$/i.test(parsed.hostname)) return null;
    return new URLSearchParams({ path: sitePath(parsed.pathname + parsed.search) }).toString();
  } catch {
    return null;
  }
}

/** 플레이스토어 주소 — 설치 뒤 처음 켤 때 `path` 로 간다 */
export function storeUrl(path: string): string {
  const referrer = `url=${encodeURIComponent(sitePath(path))}`;
  return (
    `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}` +
    `&referrer=${encodeURIComponent(referrer)}`
  );
}

/** 앱으로 여는 `intent://` 링크. 앱이 없으면 플레이스토어로 간다. */
export function appIntentUrl(path: string): string {
  return (
    `intent://www.urnplay.com${sitePath(path)}` +
    `#Intent;scheme=https;package=${ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${encodeURIComponent(storeUrl(path))};end`
  );
}
